import sortBy from "lodash.sortby";
import { useEffect, useMemo, useState } from "react";
import { match, P } from "ts-pattern";
import { BaseButton } from "@/components/atomic/BaseButton";
import Checkbox from "@/components/atomic/Checkbox";
import { Dialog, DialogContent, DialogOverlay, DialogTitle } from "@/components/atomic/Dialog";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import Separator from "@/components/atomic/Separator";
import Button from "@/components/ui/Button";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { DisplayGroup } from "@/models/displayGroup";

// layer（1始まり）に対して、親 parentPath 直下の候補を DisplayGroup.categoryPaths から構築
function buildCategoriesFromDGPaths(
  allPaths: string[],
  layer: number,
  parentPath: string,
): { name: string; value: string; }[] {
  const normParent = layer === 1 ? "/" : parentPath;

  const uniq = new Map<string, string>(); // value(key) -> name
  for (const path of allPaths) {
    if (!path.startsWith(normParent)) continue;

    const segs = path.split("/").filter(Boolean);
    if (segs.length < layer) continue;

    // layer 個目までのセグメントをキーとして採用
    const key = "/" + segs.slice(0, layer).join("/") + "/";

    // 直下かどうかの判定：prefix が parent と一致し、ちょうど layer 個で止まっているキーのみ採用
    // （parent よりも深い階層は key のスライスで吸収されるため、uniq で重複排除される）
    if (!key.startsWith(normParent)) continue;

    const name = segs[layer - 1]; // 表示名はスラッグ（末尾セグメント）
    if (!uniq.has(key)) uniq.set(key, name);
  }
  return Array.from(uniq.entries()).map(([value, name]) => ({ name, value }));
}

const Column: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
  return <div className="flex flex-col gap-5 pr-5 min-w-[220px]">{children}</div>;
};

const Item: React.FC<{
  name: string;
  value: string;
  selected: boolean;
  isFirst: boolean;
  handleItemClick: (value: string) => void;
}> = ({ name, value, isFirst, selected, handleItemClick }) => {
  return (
    <BaseButton
      variant="ghost"
      className={cn(
        "block w-[200px] h-10 pl-5 justify-start text-[#5F6368] hover:bg-[#6888E1] hover:text-white hover:font-bold truncate text-left",
        isFirst && "mt-5",
        selected && "bg-[#6888E1] text-white font-bold",
      )}
      onClick={() => handleItemClick(value)}
      title={name}
    >
      {name}
    </BaseButton>
  );
};

interface Props {
  className?: string;
  displayGroups: DisplayGroup[]; // 例で渡された配列
  initialSelectedPaths?: string[]; // 初期選択されているカテゴリーパス
  isOpenModal: boolean;
  setIsOpenModal: (value: boolean) => void;
  submit: (displayGroupId: string, categoryPaths: string[]) => void;
}

const CategorySelectModal: React.FC<Props> = ({
  className = "",
  displayGroups,
  initialSelectedPaths = [],
  isOpenModal,
  setIsOpenModal,
  submit,
}) => {
  const { categoryMaxLevel } = useWorkspaces();

  // 0番目: 表示グループID、1番目以降: カテゴリパス
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    new Array(categoryMaxLevel).fill(""),
  );
  // 最下層（子が無い層）の複数選択 - 表示グループに関係なく全体で管理
  const [leafSelections, setLeafSelections] = useState<string[]>([]);

  useEffect(() => {
    setSelectedCategories(new Array(categoryMaxLevel).fill(""));
  }, [categoryMaxLevel]);

  // モーダルが開かれた時に初期選択をセット
  useEffect(() => {
    if (isOpenModal && initialSelectedPaths.length > 0) {
      // 初期選択パスをそのままセット（表示グループに関係なく）
      setLeafSelections(initialSelectedPaths);

      // 最初の表示グループを選択（デフォルト）
      if (displayGroups.length > 0) {
        const categories = new Array(categoryMaxLevel).fill("");
        categories[0] = displayGroups[0].id;
        setSelectedCategories(categories);
      }
    } else if (isOpenModal && initialSelectedPaths.length === 0) {
      // 初期選択がない場合はクリア
      setLeafSelections([]);
      if (displayGroups.length > 0) {
        const categories = new Array(categoryMaxLevel).fill("");
        categories[0] = displayGroups[0].id;
        setSelectedCategories(categories);
      }
    }
  }, [isOpenModal, initialSelectedPaths, displayGroups, categoryMaxLevel]);

  const selectedDisplayGroup = useMemo(
    () => displayGroups.find((dg) => dg.id === selectedCategories[0]),
    [displayGroups, selectedCategories],
  );

  // 未選択の最初の層（=入力中の層）
  const firstEmptyIndex = selectedCategories.indexOf("");
  const currentLayer = firstEmptyIndex === -1 ? categoryMaxLevel : firstEmptyIndex;

  // 指定層の候補（landingPaths 不要版：DisplayGroup.categoryPaths から構築）
  const getItems = (layer: number) => {
    if (layer === 0) {
      return displayGroups?.map((dg) => ({ name: dg.name, value: dg.id })) ?? [];
    }
    if (!selectedDisplayGroup) return [];
    if (layer > currentLayer) return [];

    const selectedCategoryPath = selectedCategories[layer - 1];
    const parentPath = layer === 1 ? "/" : selectedCategoryPath;
    const items = buildCategoriesFromDGPaths(selectedDisplayGroup.categoryPaths, layer, parentPath);

    return sortBy(items, ["name"]);
  };

  // 単一選択（子がある場合の通常遷移）
  const handleClickItem = (layer: number, value: string) => {
    const categories = selectedCategories.map((c, i) =>
      match(i)
        .with(P.when((n) => n < layer), () => c)
        .with(P.when((n) => n === layer), () => value)
        .otherwise(() => "")
    );
    setSelectedCategories(categories);
  };

  // 葉（子なし）のチェック切り替え - 表示グループに関係なく全体で管理
  const toggleLeaf = (value: string) => {
    setLeafSelections((prev) => {
      // すでに選択されているかチェック（完全一致）
      if (prev.includes(value)) {
        // 選択を解除
        return prev.filter((v) => v !== value);
      } else {
        // 新規選択
        return [...prev, value];
      }
    });
  };

  // 決定ボタン活性条件：葉が1つ以上チェックされている
  const isSubmitDisabled = leafSelections.length === 0;

  const handleClickSubmit = () => {
    const selectedDisplayGroupId = selectedCategories[0];
    const categoryPaths = leafSelections.slice();
    setIsOpenModal(false);
    // 直接選択されたものを反映（追加ではなく置き換え）
    submit(selectedDisplayGroupId, categoryPaths);

    // クリア
    setSelectedCategories(new Array(categoryMaxLevel).fill(""));
    setLeafSelections([]);
  };

  // モーダルが閉じられた時のハンドラ
  const handleOpenChange = (open: boolean) => {
    setIsOpenModal(open);
    if (!open) {
      // モーダルを閉じる時は選択状態をクリア
      setSelectedCategories(new Array(categoryMaxLevel).fill(""));
      setLeafSelections([]);
    }
  };

  return (
    <Dialog open={isOpenModal} onOpenChange={handleOpenChange}>
      <DialogOverlay className={cn("bg-[#FFFFFF00] left-0")} />
      <DialogContent className={cn("max-w-[1110px] w-[calc(100vw-120px)]", className)}>
        <DialogTitle className="flex flex-row justify-between mb-7">
          <div className="leading-[40px]">カテゴリーの選択</div>
          <Button
            className="w-50 h-10 text-base text-[#5F6368]"
            disabled={isSubmitDisabled}
            onClick={handleClickSubmit}
          >
            決定
          </Button>
        </DialogTitle>
        <Separator />
        <ScrollArea>
          <div className="flex gap-5">
            {selectedCategories.map((selected, i) => {
              const items = getItems(i);
              return (
                <div className="flex" key={`${selected}-${i}`}>
                  <ScrollArea className="h-[calc(100vh-360px)]">
                    <Column>
                      {items.map((item, j) => {
                        // レイヤー0（Display Group）は常に単一選択ボタン
                        if (i === 0) {
                          return (
                            <Item
                              key={item.value}
                              name={item.name}
                              value={item.value}
                              isFirst={j === 0}
                              selected={item.value === selectedCategories[i]}
                              handleItemClick={(value: string) => handleClickItem(i, value)}
                            />
                          );
                        }

                        // レイヤー1以降：最大レベルに達したらチェックボックス、そうでなければボタン
                        const isMaxLevel = i >= categoryMaxLevel - 1;
                        if (isMaxLevel) {
                          const checked = leafSelections.includes(item.value);
                          return (
                            <label
                              key={item.value}
                              className={cn(
                                "flex items-center w-[200px] h-10 pl-5 pr-2 rounded-[5px] cursor-pointer truncate",
                                j === 0 && "mt-5",
                                checked
                                  ? "bg-[#6888E1] font-bold text-white hover:bg-[#5677D0]"
                                  : "text-[#5F6368] hover:bg-[#EFF2FD]",
                              )}
                              title={item.name}
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => toggleLeaf(item.value)}
                                variant={checked ? "inverted" : "default"}
                                className="mr-3"
                              />
                              <span className="truncate">{item.name}</span>
                            </label>
                          );
                        }

                        return (
                          <Item
                            key={item.value}
                            name={item.name}
                            value={item.value}
                            isFirst={j === 0}
                            selected={item.value === selectedCategories[i]}
                            handleItemClick={(value: string) => handleClickItem(i, value)}
                          />
                        );
                      })}
                    </Column>
                    <ScrollBar orientation="vertical" />
                  </ScrollArea>
                  <Separator orientation="vertical" />
                </div>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" className="hidden" />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CategorySelectModal;
